import apiClient from '../utils/apiClient';
import { apiUtils, dateUtils } from '../utils/commonUtils';

export const fetchTenants = async (filters = {}) => {
  try {
    const {
      search = '',
      status = '',
      subscription_plan = '',
      expired_before = '',
      expired_after = '',
      sort_by = '',
      per_page = 15
    } = filters;

    const params = {};
    
    if (search) params.search = search;
    if (status) params.status = status;
    if (subscription_plan) params.subscription_plan = subscription_plan;
    if (expired_before) params.expired_before = expired_before;
    if (expired_after) params.expired_after = expired_after;
    if (sort_by) params.sort_by = sort_by;
    if (per_page) params.per_page = per_page;

    const response = await apiClient.get('/tenants', { params });

    if (response.data.status === 'success') {
      return {
        success: true,
        data: response.data.data,
        meta: response.data.meta,
        message: response.data.message
      };
    } else {
      throw new Error(response.data.message || 'Failed to fetch tenants');
    }
  } catch (error) {
    return apiUtils.handleError(error, 'Failed to fetch tenants');
  }
};

export const transformTenantData = (apiTenant) => {
  return {
    id: apiTenant.id,
    namaTenant: apiTenant.name,
    domainUnik: apiTenant.domain,
    email: apiTenant.pics && apiTenant.pics.length > 0 ? apiTenant.pics[0].email : '',
    nomorTelepon: apiTenant.pics && apiTenant.pics.length > 0 ? apiTenant.pics[0].phone : '',
    alamat: apiTenant.address,
    status: apiTenant.status === 'active' ? 'Active' : 'Non Active',
    subscriptionPlan: apiTenant.package ? apiTenant.package.name : 'Basic',
    tanggalMulai: dateUtils.formatISO(apiTenant.subscription_start_at),
    tanggalBerakhir: dateUtils.formatISO(apiTenant.subscription_expired_at),
    gracePeriod: apiTenant.grace_period_days || 0,
    diskon: parseFloat(apiTenant.diskon) || 0,
    lampiran: apiTenant.additional_attachment || '',
    remark: apiTenant.remark || '',
    referralSourceId: apiTenant.id_referral_source || '',
    pics: apiTenant.pics ? apiTenant.pics.map(pic => ({
      id: pic.id,
      nama: pic.name,
      nomorTelepon: pic.phone,
      email: pic.email
    })) : [],
    harga_package: apiTenant.harga_package,
    implementation_cost: apiTenant.implementation_cost,
    jumlah_student: apiTenant.jumlah_student || 0,
    total: apiTenant.total,
    additional_attachment_url: apiTenant.additional_attachment_url,
    package: apiTenant.package,
    invitations: Array.isArray(apiTenant.invitations)
      ? apiTenant.invitations.map(inv => ({
          id: inv.id,
          name: inv.name,
          email: inv.email,
          phone: inv.phone,
          status: inv.status
        }))
      : []
  };
};

export const transformTenantsData = (apiTenants) => {
  return apiTenants.map(transformTenantData);
};

export const deleteTenant = async (tenantId) => {
  try {
    const response = await apiClient.delete(`/tenants/${tenantId}`);

    if (response.data.status === 'success') {
      return {
        success: true,
        message: response.data.message || 'Tenant deleted successfully'
      };
    } else {
      throw new Error(response.data.message || 'Failed to delete tenant');
    }
  } catch (error) {
    return apiUtils.handleError(error, 'Failed to delete tenant');
  }
};

export const updateTenantStatus = async (tenantId, status) => {
  try {
    const response = await apiClient.put(`/tenants/${tenantId}`, {
      status: status
    });

    if (response.data.status === 'success') {
      return {
        success: true,
        message: response.data.message || 'Tenant status updated successfully'
      };
    } else {
      throw new Error(response.data.message || 'Failed to update tenant status');
    }
  } catch (error) {
    return apiUtils.handleError(error, 'Failed to update tenant status');
  }
};

export const updateTenantPics = async (tenantId, pics) => {
  try {
    const response = await apiClient.put(`/tenants/${tenantId}/pics`, {
      pics: pics
    });

    if (response.data.status === 'success') {
      return {
        success: true,
        message: response.data.message || 'PICs updated successfully'
      };
    } else {
      throw new Error(response.data.message || 'Failed to update PICs');
    }
  } catch (error) {
    return apiUtils.handleError(error, 'Failed to update PICs');
  }
};

// Create new tenant
export const createTenant = async (tenantData) => {
  try {
    const {
      name,
      domain,
      address,
      status,
      remark,
      subscription_start_at,
      subscription_expired_at,
      grace_period_days,
      additional_attachment,
      id_referral_source,
      id_package,
      harga_package,
      diskon,
      implementation_cost,
      jumlah_student,
      total,
      pics = [],
      user_invitation = []
    } = tenantData;

    // Prepare form data
    const formData = new FormData();

    // Basic tenant information
    formData.append('name', name);
    formData.append('domain', domain);
    formData.append('address', address);
    formData.append('status', status);
    formData.append('remark', remark || '');
    formData.append('subscription_start_at', subscription_start_at);
    formData.append('subscription_expired_at', subscription_expired_at);
    formData.append('grace_period_days', grace_period_days);
    formData.append('id_referral_source', id_referral_source);
    formData.append('id_package', id_package);
    formData.append('harga_package', harga_package);
    formData.append('diskon', diskon);
    formData.append('implementation_cost', implementation_cost);
    formData.append('jumlah_student', jumlah_student ?? 0);
    formData.append('total', total);

    // Add additional attachment if provided
    if (additional_attachment) {
      formData.append('additional_attachment', additional_attachment);
    }

    // Add PICs data
    pics.forEach((pic, index) => {
      formData.append(`pic[${index}][name]`, pic.name);
      formData.append(`pic[${index}][email]`, pic.email);
      formData.append(`pic[${index}][phone]`, pic.phone);
    });

    // Add user invitations if provided
    user_invitation.forEach((inv, index) => {
      formData.append(`user_invitation[${index}][name]`, inv.name);
      formData.append(`user_invitation[${index}][email]`, inv.email);
      formData.append(`user_invitation[${index}][phone]`, inv.phone);
    });

    const response = await apiClient.post('/tenants', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    if (response.data.status === 'success') {
      return {
        success: true,
        data: response.data.data,
        message: response.data.message || 'Tenant created successfully'
      };
    } else {
      throw new Error(response.data.message || 'Failed to create tenant');
    }
  } catch (error) {
    return apiUtils.handleError(error, 'Failed to create tenant');
  }
};

// Get tenant by ID
export const getTenantById = async (tenantId) => {
  try {
    const response = await apiClient.get(`/tenants/${tenantId}`);

    if (response.data.status === 'success') {
      return {
        success: true,
        data: transformTenantData(response.data.data),
        message: response.data.message || 'Tenant fetched successfully'
      };
    } else {
      throw new Error(response.data.message || 'Failed to fetch tenant');
    }
  } catch (error) {
    return apiUtils.handleError(error, 'Failed to fetch tenant');
  }
};

// Update tenant
export const updateTenant = async (tenantId, tenantData) => {
  try {
    const {
      name,
      domain,
      address,
      status,
      remark,
      subscription_start_at,
      subscription_expired_at,
      grace_period_days,
      additional_attachment,
      id_referral_source,
      id_package,
      harga_package,
      diskon,
      implementation_cost,
      jumlah_student,
      total,
      pics = [],
      user_invitation = []
    } = tenantData;

    // Prepare form data
    const formData = new FormData();

    // Basic tenant information
    formData.append('name', name);
    formData.append('domain', domain);
    formData.append('address', address);
    formData.append('status', status);
    formData.append('remark', remark || '');
    formData.append('subscription_start_at', subscription_start_at);
    formData.append('subscription_expired_at', subscription_expired_at);
    formData.append('grace_period_days', grace_period_days);
    formData.append('id_referral_source', id_referral_source);
    formData.append('id_package', id_package);
    formData.append('harga_package', harga_package);
    formData.append('diskon', diskon);
    formData.append('implementation_cost', implementation_cost);
    formData.append('jumlah_student', jumlah_student ?? 0);
    formData.append('total', total);

    // Add additional attachment if provided
    if (additional_attachment) {
      formData.append('additional_attachment', additional_attachment);
    }

    // Add PICs data
    pics.forEach((pic, index) => {
      formData.append(`pic[${index}][name]`, pic.name);
      formData.append(`pic[${index}][email]`, pic.email);
      formData.append(`pic[${index}][phone]`, pic.phone);
    });

    // Add user invitations if provided
    user_invitation.forEach((inv, index) => {
      formData.append(`user_invitation[${index}][name]`, inv.name);
      formData.append(`user_invitation[${index}][email]`, inv.email);
      formData.append(`user_invitation[${index}][phone]`, inv.phone);
    });

    const response = await apiClient.put(`/tenants/${tenantId}`, formData);

    if (response.data.status === 'success') {
      return {
        success: true,
        data: response.data.data,
        message: response.data.message || 'Tenant updated successfully'
      };
    } else {
      throw new Error(response.data.message || 'Failed to update tenant');
    }
  } catch (error) {
    return apiUtils.handleError(error, 'Failed to update tenant');
  }
};

export const createTenantPic = async ({ id_tenant, name, email, phone }) => {
  try {
    const response = await apiClient.post('/tenant-pics', {
      id_tenant,
      name,
      email,
      phone
    });

    if ((response.status >= 200 && response.status < 300)) {
      return {
        success: true,
        data: response.data?.data,
        message: response.data?.message || 'PIC created successfully'
      };
    } else {
      throw new Error(response.data?.message || 'Failed to create PIC');
    }
  } catch (error) {
    return apiUtils.handleError(error, 'Failed to create PIC');
  }
};


// Create tenant user invitation
export const createTenantUserInvitation = async ({ id_tenant, name, email, phone }) => {
  try {
    const response = await apiClient.post('/tenant-user-invitations', {
      id_tenant,
      name,
      email,
      phone
    });

    if ((response.status >= 200 && response.status < 300)) {
      return {
        success: true,
        data: response.data?.data,
        message: response.data?.message || 'Tenant user invitation created successfully'
      };
    } else {
      throw new Error(response.data?.message || 'Failed to create tenant user invitation');
    }
  } catch (error) {
    return apiUtils.handleError(error, 'Failed to create tenant user invitation');
  }
};

export const fetchTenantReferralSources = async () => {
  try {
    const response = await apiClient.get('/tenant-referral-sources');

    if (response.data.status === 'success') {
      return {
        success: true,
        data: response.data.data,
        message: response.data.message || 'Referral sources fetched successfully'
      };
    } else {
      throw new Error(response.data.message || 'Failed to fetch referral sources');
    }
  } catch (error) {
    return apiUtils.handleError(error, 'Failed to fetch referral sources');
  }
};

export const updateTenantReferralSource = async (referralSourceId, referralSourceData) => {
  try {
    const response = await apiClient.put(`/tenant-referral-sources/${referralSourceId}`, referralSourceData);

    if (response.data.status === 'success') {
      return {
        success: true,
        data: response.data.data,
        message: response.data.message || 'Referral source updated successfully'
      };
    } else {
      throw new Error(response.data.message || 'Failed to update referral source');
    }
  } catch (error) {
    return apiUtils.handleError(error, 'Failed to update referral source');
  }
};

export const createTenantUser = async ({ email, password, tenant_id }) => {
  try {
    const response = await apiClient.post('/user-backoffice', {
      email,
      password,
      tenant_id,
    });

    if (response.data.status === 'success') {
      return {
        success: true,
        data: response.data.data,
        message: response.data.message || 'User created successfully'
      };
    } else {
      throw new Error(response.data.message || 'Failed to create user');
    }
  } catch (error) {
    return apiUtils.handleError(error, 'Failed to create user');
  }
};